import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { getFirestore, setDoc, doc } from "firebase/firestore";
import app from "../firebase";
import { Helmet } from "react-helmet-async";
import { useToast } from "../context/ToastContext"; // ✅ Custom Toast

const auth = getAuth(app);
const db = getFirestore(app);

const getFriendlyFirebaseError = (code) => {
  const map = {
    "auth/email-already-in-use": "Email is already in use.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/weak-password": "Password should be at least 6 characters.",
  };
  return map[code] || "An unexpected error occurred. Please try again.";
};

export default function AdminSignup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast(); // ✅

  const handleSignup = async () => {
    setLoading(true);
    try {
      const userCred = await createUserWithEmailAndPassword(auth, email, password);

      // Create user record in Firestore
      await setDoc(doc(db, "users", userCred.user.uid), {
        email,
        createdAt: new Date(),
        role: "User",
      });

      addToast("Account created successfully!", "success"); // ✅
      navigate("/admin");
    } catch (error) {
      addToast(getFriendlyFirebaseError(error.code), "error"); // ✅
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Your Choice Associates | Admin Signup</title>
        <meta name="description" content="Register for an admin account with Your Choice Associates." />
        <link rel="canonical" href="https://yourchoiceassociates.org/admin-signup" />
      </Helmet>

      {/* Topbar */}
      <div className="w-full bg-[#00203a] text-white py-4 px-6 flex items-center justify-between shadow-md">
        <img src="/images/logo.png" alt="Logo" className="h-10" />
        <span className="text-sm opacity-80">Admin Signup</span>
      </div>

      {/* Main Content */}
      <div className="flex flex-1">
        <div className="w-full md:w-1/2 flex flex-col justify-center px-10 md:px-20 bg-white">
          <div className="mt-12 md:mt-0">
            <h2 className="text-3xl font-extrabold text-[#00203a] mb-3">Create an Account</h2>
            <p className="text-sm text-gray-500 mb-8">Sign up to access the admin dashboard.</p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#00203a]"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#00203a]"
                />
              </div>

              <button
                onClick={handleSignup}
                disabled={loading}
                className="w-full bg-[#00203a] text-white py-3 rounded-lg hover:bg-[#00172e] transition font-semibold shadow-md"
              >
                {loading ? "Creating account..." : "Sign Up"}
              </button>

              <p className="text-sm text-center text-gray-600 pt-2">
                Already have an account?{" "}
                <a href="/admin-login" className="text-[#00203a] font-medium hover:underline">
                  Log in here
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="hidden md:block md:w-1/2 h-screen">
          <img src="/images/hero.png" alt="Hero Visual" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  );
}
